// Employee Commission — brand-level payroll report (Feb 2026).
// Lives inside Brand Reports → Accounting as the first item under the
// tab. Pulls aggregated commission from `staff_commission_ledger`
// (never recalculates) and surfaces it as a per-staff table with
// expandable line-item breakdowns and PDF / Excel exports.
//
// Period filter: This Month (default) · Last Month · This Year ·
// Custom Date Range (no restriction). All dates resolved in Bali TZ.
//
// Branch filter (Owner / Admin only, Iter-406): a chip next to the
// period picker exposes a multi-select over every branch surfaced in
// the ledger response. Fetch stays brand-wide — the filter is a
// pure DISPLAY cut (hides staff + lines + branch totals outside the
// current selection; "All branches" is the default).
//
// Branch sub-totals (Iter-406): below the brand summary row we render
// one line per branch with activity in the current filtered view, so
// finance can see each location's share at a glance.
//
// Permission for the fetch: enforced server-side by
// `routes/reports_employee_commission.py::_require_brand_accounting`.
// Owner always sees.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  ChevronDown, ChevronRight, FileDown, FileSpreadsheet, RefreshCw,
  Building2, Check, Layers,
} from "lucide-react";
import { toast } from "sonner";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import {
  downloadReportPdf, downloadReportXlsx,
} from "@/pages/staff/branch/reports/exportUtils";
import { useAuth } from "@/lib/auth";
import { isOwnerOrAdmin } from "@/lib/AuthEngine";

const PRESETS = [
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "this_year",  label: "This Year" },
  { key: "custom",     label: "Custom Date Range" },
];

const BALI_TZ = "Asia/Makassar";
const ALL_BRANCHES = "__all__";

function todayBali() {
  return new Date().toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}
function parseBali(dateStr) {
  // Anchored at Bali NOON (04:00 UTC) — a midnight anchor sits at 16:00 UTC
  // on the PREVIOUS day, so every `getUTC*` / `toISOString()` read came back
  // one day early and rolled "This Month" / "This Year" into the prior
  // period on the 1st of the month.
  return new Date(`${dateStr}T12:00:00+08:00`);
}
function fmt(d) {
  return d.toLocaleDateString("en-CA", { timeZone: BALI_TZ });
}
function rangeForPreset(preset, customFrom, customTo) {
  const today = todayBali();
  switch (preset) {
    case "this_month": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
      const end = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)));
      return { date_from: start, date_to: end };
    }
    case "last_month": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1)));
      const end = fmt(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)));
      return { date_from: start, date_to: end };
    }
    case "this_year": {
      const d = parseBali(today);
      const start = fmt(new Date(Date.UTC(d.getUTCFullYear(), 0, 1)));
      const end = fmt(new Date(Date.UTC(d.getUTCFullYear(), 11, 31)));
      return { date_from: start, date_to: end };
    }
    case "custom":
      return { date_from: customFrom, date_to: customTo };
    default:
      return { date_from: today, date_to: today };
  }
}

function prettyDate(d) {
  if (!d) return "—";
  try {
    return new Date(`${d}T00:00:00+08:00`).toLocaleDateString("en-GB", {
      timeZone: BALI_TZ, day: "2-digit", month: "short", year: "numeric",
    });
  } catch { return d; }
}

export default function EmployeeCommissionReport() {
  const { staff } = useAuth();
  const canFilterByBranch = isOwnerOrAdmin(staff);

  const [preset, setPreset] = useState("this_month");
  const [customFrom, setCustomFrom] = useState(todayBali());
  const [customTo, setCustomTo] = useState(todayBali());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [branches, setBranches] = useState([]);
  const [busyExport, setBusyExport] = useState(null); // "pdf" | "xlsx" | null
  // Multi-select branch filter — empty Set == "All branches".
  const [branchFilter, setBranchFilter] = useState(() => new Set());

  const range = useMemo(
    () => rangeForPreset(preset, customFrom, customTo),
    [preset, customFrom, customTo],
  );

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const primaryBranch = branches[0] || null;
  const fm = useCallback((n) => formatBranchMoney(primaryBranch, Number(n) || 0), [primaryBranch]);

  const load = useCallback(async () => {
    if (!range.date_from || !range.date_to) return;
    setLoading(true);
    try {
      const r = await staffApi.get("/reports/brand/employee-commission", { params: range });
      setData(r.data);
      setExpanded({});
    } catch (err) {
      setData(null);
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => setExpanded((e) => ({ ...e, [sid]: !e[sid] }));

  // ------------------------------------------------------------------
  // Branch universe — the set of branches that have activity in the
  // currently-fetched response. The filter chip only lists these
  // branches, never the brand's full /branches catalog, so finance
  // doesn't tick a branch that would show nothing.
  // ------------------------------------------------------------------
  const branchUniverse = useMemo(() => {
    if (!data) return [];
    const byId = new Map();
    for (const s of (data.staff || [])) {
      for (const line of (s.lines || [])) {
        if (line.branch_id && !byId.has(line.branch_id)) {
          byId.set(line.branch_id, line.branch_name || line.branch_id);
        }
      }
    }
    return Array.from(byId, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  // ------------------------------------------------------------------
  // Filtered view — apply the branch filter to every staff row's
  // lines, drop staff whose filtered line list is empty, and
  // recompute per-staff + brand + per-branch totals from the lines
  // that survive. The filter is DISPLAY-ONLY — no re-fetch, no
  // mutation of the server response.
  // ------------------------------------------------------------------
  const view = useMemo(() => {
    if (!data) {
      return {
        staff: [],
        totals: { earned: 0, deductions: 0, net: 0 },
        branchTotals: [],
      };
    }
    const activeFilter = branchFilter.size > 0;
    const branchTotals = new Map();  // branch_id → {branch_name, earned, deductions, net}

    const filteredStaff = [];
    for (const s of (data.staff || [])) {
      const lines = (s.lines || []).filter(
        (ln) => !activeFilter || branchFilter.has(ln.branch_id),
      );
      if (lines.length === 0) continue;
      let earned = 0;
      let deducted = 0;
      const branchIdsOnStaff = new Set();
      const branchNamesOnStaff = [];
      for (const ln of lines) {
        const amt = Number(ln.amount) || 0;
        if (ln.record_type === "deduction") deducted += Math.abs(amt);
        else earned += amt;
        if (ln.branch_id && !branchIdsOnStaff.has(ln.branch_id)) {
          branchIdsOnStaff.add(ln.branch_id);
          if (ln.branch_name) branchNamesOnStaff.push(ln.branch_name);
        }
        // Branch sub-totals bucket.
        if (ln.branch_id) {
          const bt = branchTotals.get(ln.branch_id) || {
            branch_id: ln.branch_id,
            branch_name: ln.branch_name || ln.branch_id,
            earned: 0,
            deductions: 0,
          };
          if (ln.record_type === "deduction") bt.deductions += Math.abs(amt);
          else bt.earned += amt;
          branchTotals.set(ln.branch_id, bt);
        }
      }
      filteredStaff.push({
        ...s,
        lines,
        branch_ids: Array.from(branchIdsOnStaff),
        branch_names: branchNamesOnStaff,
        total_earned: Number(earned.toFixed(2)),
        total_deductions: Number(deducted.toFixed(2)),
        net_commission: Number((earned - deducted).toFixed(2)),
      });
    }
    filteredStaff.sort((a, b) => b.net_commission - a.net_commission);

    const totalEarned = filteredStaff.reduce((n, s) => n + s.total_earned, 0);
    const totalDeducted = filteredStaff.reduce((n, s) => n + s.total_deductions, 0);
    const branchTotalsList = Array.from(branchTotals.values()).map((b) => ({
      ...b,
      earned: Number(b.earned.toFixed(2)),
      deductions: Number(b.deductions.toFixed(2)),
      net: Number((b.earned - b.deductions).toFixed(2)),
    })).sort((a, b) => b.net - a.net);

    return {
      staff: filteredStaff,
      totals: {
        earned: Number(totalEarned.toFixed(2)),
        deductions: Number(totalDeducted.toFixed(2)),
        net: Number((totalEarned - totalDeducted).toFixed(2)),
      },
      branchTotals: branchTotalsList,
    };
  }, [data, branchFilter]);

  // ------------------------------------------------------------------
  // Export spec builder — flattens the filtered view into one rows
  // array; adds a per-branch block + brand total footer.
  // ------------------------------------------------------------------
  const buildExportSpec = useCallback(() => {
    if (!data) return null;
    const columns = [
      { header: "Staff / Line", key: "name" },
      { header: "Role", key: "role" },
      { header: "Branch", key: "branch" },
      { header: "Date", key: "date" },
      { header: "Customer", key: "customer" },
      { header: "Treatment", key: "treatment" },
      {
        header: "Earned", key: "earned", align: "right",
        numeric: true, format: fm, numFmt: "#,##0",
      },
      {
        header: "Deductions", key: "deductions", align: "right",
        numeric: true, format: fm, numFmt: "#,##0",
      },
      {
        header: "Net", key: "net", align: "right",
        numeric: true, format: fm, numFmt: "#,##0",
      },
    ];
    const rows = [];
    for (const s of view.staff) {
      rows.push({
        name: s.staff_name,
        role: s.role || "—",
        branch: (s.branch_names || []).join(", ") || "—",
        date: "",
        customer: "",
        treatment: "",
        earned: s.total_earned,
        deductions: s.total_deductions,
        net: s.net_commission,
      });
      for (const line of s.lines) {
        const isDed = line.record_type === "deduction";
        rows.push({
          name: `   ↳ ${line.order_number || "—"}`,
          role: "",
          branch: line.branch_name || "",
          date: prettyDate(line.date),
          customer: line.customer_name || "—",
          treatment: line.treatment_name || "—",
          earned: isDed ? "" : line.amount,
          deductions: isDed ? Math.abs(line.amount) : "",
          net: "",
        });
      }
    }
    // Spacer + per-branch sub-totals block for the export.
    if (view.branchTotals.length > 0) {
      rows.push({
        name: "", role: "", branch: "", date: "", customer: "",
        treatment: "", earned: "", deductions: "", net: "",
      });
      rows.push({
        name: "BRANCH TOTALS", role: "", branch: "", date: "",
        customer: "", treatment: "", earned: "", deductions: "", net: "",
      });
      for (const bt of view.branchTotals) {
        rows.push({
          name: bt.branch_name,
          role: "", branch: "", date: "", customer: "", treatment: "",
          earned: bt.earned,
          deductions: bt.deductions,
          net: bt.net,
        });
      }
    }

    const periodSlug = (data.period_label || "")
      .replace(/\s+→\s+/g, "_to_")
      .replace(/\s+/g, "-");
    const filterSuffix = branchFilter.size > 0 && branchFilter.size < branchUniverse.length
      ? `-${view.branchTotals.map((b) => b.branch_name).join("-").replace(/\s+/g, "-")}`
      : "";
    return {
      title: "Employee Commission",
      filename: `Employee-Commission-${periodSlug || "report"}${filterSuffix}`,
      subtitle: `Period: ${data.period_label || ""}${branchFilter.size > 0 ? ` · Branches: ${view.branchTotals.map((b) => b.branch_name).join(", ")}` : ""}`,
      range: { date_from: data.date_from, date_to: data.date_to },
      columns,
      rows,
      footerRow: [
        "TOTAL", "", "", "", "", "",
        Number(view.totals.earned || 0),
        Number(view.totals.deductions || 0),
        Number(view.totals.net || 0),
      ],
    };
  }, [data, view, fm, branchFilter, branchUniverse.length]);

  const runExport = async (format) => {
    const spec = buildExportSpec();
    if (!spec) return;
    setBusyExport(format);
    try {
      if (format === "pdf") await downloadReportPdf(spec);
      else downloadReportXlsx(spec);
    } catch (e) {
      toast.error(`Could not build the ${format.toUpperCase()}. Try refreshing and retry.`);
      // eslint-disable-next-line no-console
      console.error(`${format} export failed`, e);
    } finally {
      setBusyExport(null);
    }
  };

  const hasData = view.staff.length > 0;
  const totals = view.totals;
  const toggleBranch = (bid) => {
    setBranchFilter((prev) => {
      const next = new Set(prev);
      if (next.has(bid)) next.delete(bid);
      else next.add(bid);
      return next;
    });
  };
  const clearBranchFilter = () => setBranchFilter(new Set());

  const branchFilterLabel = branchFilter.size === 0
    ? "All branches"
    : branchFilter.size === 1
      ? view.branchTotals[0]?.branch_name || `${branchFilter.size} branch`
      : `${branchFilter.size} branches`;

  return (
    <div className="space-y-4" data-testid="employee-commission-report">
      {/* Header bar — title + export buttons */}
      <div className="flex items-start gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">Employee Commission</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Payroll roll-up pulled live from the commission ledger.
            {data?.period_label ? ` ${data.period_label}.` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-9"
            onClick={() => runExport("pdf")}
            disabled={!hasData || busyExport !== null}
            data-testid="emp-commission-download-pdf"
          >
            <FileDown className="w-4 h-4" />
            {busyExport === "pdf" ? "Preparing…" : "Download PDF"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 h-9"
            onClick={() => runExport("xlsx")}
            disabled={!hasData || busyExport !== null}
            data-testid="emp-commission-download-excel"
          >
            <FileSpreadsheet className="w-4 h-4" />
            {busyExport === "xlsx" ? "Preparing…" : "Download Excel"}
          </Button>
        </div>
      </div>

      {/* Period + Branch filter card */}
      <Card className="rounded-lg">
        <CardContent className="p-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-muted-foreground mr-1">Period</span>
          <Select value={preset} onValueChange={setPreset}>
            <SelectTrigger className="w-56" data-testid="emp-commission-period">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.map((p) => (
                <SelectItem
                  key={p.key}
                  value={p.key}
                  data-testid={`emp-commission-period-${p.key}`}
                >
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {preset === "custom" && (
            <>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-40"
                data-testid="emp-commission-custom-from"
              />
              <span className="text-xs text-muted-foreground">to</span>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-40"
                data-testid="emp-commission-custom-to"
              />
            </>
          )}

          {canFilterByBranch && branchUniverse.length > 0 && (
            <>
              <span className="text-xs uppercase tracking-wider text-muted-foreground ml-2 mr-1">Branches</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-9 gap-1.5"
                    data-testid="emp-commission-branch-filter"
                  >
                    <Building2 className="w-3.5 h-3.5" />
                    <span className="text-xs">{branchFilterLabel}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between px-2 py-1.5 rounded hover:bg-muted text-xs"
                    onClick={clearBranchFilter}
                    data-testid="emp-commission-branch-all"
                  >
                    <span className="font-medium">All branches</span>
                    {branchFilter.size === 0 && <Check className="w-3.5 h-3.5 text-primary" />}
                  </button>
                  <div className="h-px bg-border my-1" />
                  <div className="max-h-64 overflow-y-auto">
                    {branchUniverse.map((b) => (
                      <label
                        key={b.id}
                        className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-xs"
                        data-testid={`emp-commission-branch-opt-${b.id}`}
                      >
                        <Checkbox
                          checked={branchFilter.has(b.id)}
                          onCheckedChange={() => toggleBranch(b.id)}
                        />
                        <span className="truncate">{b.name}</span>
                      </label>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </>
          )}

          <Button
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="gap-1 ml-auto"
            data-testid="emp-commission-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </CardContent>
      </Card>

      {/* Results table */}
      <Card className="rounded-lg overflow-hidden">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="uppercase text-[10px] tracking-wider w-8" />
                <TableHead className="uppercase text-[10px] tracking-wider">Staff Name</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Role</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider">Branch</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Total Earned</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Total Deductions</TableHead>
                <TableHead className="uppercase text-[10px] tracking-wider text-right">Net Commission</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!hasData ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-8">
                    {loading
                      ? "Loading…"
                      : branchFilter.size > 0
                        ? "No commission activity for the selected branches in this period."
                        : "No commission activity in the selected period."}
                  </TableCell>
                </TableRow>
              ) : view.staff.map((s) => (
                <React.Fragment key={s.staff_id}>
                  <TableRow
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() => toggle(s.staff_id)}
                    data-testid={`emp-commission-row-${s.staff_id}`}
                  >
                    <TableCell className="w-8 align-middle">
                      {expanded[s.staff_id] ? (
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="text-sm font-medium">{s.staff_name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.role || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {(s.branch_names || []).join(", ") || "—"}
                    </TableCell>
                    <TableCell
                      className="text-right text-sm tabular-nums"
                      data-testid={`emp-commission-earned-${s.staff_id}`}
                    >
                      {fm(s.total_earned)}
                    </TableCell>
                    <TableCell
                      className="text-right text-sm tabular-nums text-red-600"
                      data-testid={`emp-commission-ded-${s.staff_id}`}
                    >
                      {s.total_deductions > 0 ? `−${fm(s.total_deductions)}` : fm(0)}
                    </TableCell>
                    <TableCell
                      className="text-right text-sm font-semibold tabular-nums"
                      data-testid={`emp-commission-net-${s.staff_id}`}
                    >
                      {fm(s.net_commission)}
                    </TableCell>
                  </TableRow>
                  {expanded[s.staff_id] && (
                    <TableRow className="bg-muted/20">
                      <TableCell />
                      <TableCell colSpan={6} className="py-2">
                        {(s.tier_summary || []).length > 0 && (
                          <div
                            className="mb-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-2.5 space-y-1.5"
                            data-testid={`emp-commission-tier-block-${s.staff_id}`}
                          >
                            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-emerald-900 font-semibold">
                              <Layers className="w-3 h-3" /> Tiered commission profile
                            </div>
                            {s.tier_summary.map((t) => (
                              <div
                                key={`${s.staff_id}-${t.month}`}
                                className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-emerald-900"
                                data-testid={`emp-commission-tier-${s.staff_id}-${t.month}`}
                              >
                                <span className="font-medium">{t.month_label}</span>
                                <span data-testid={`emp-commission-tier-reached-${s.staff_id}-${t.month}`}>
                                  {t.tier_label} · {t.rate}%
                                  {t.tier_index > 0 ? ` (crossed ${fm(t.threshold)})` : ` (base ${t.base_rate}%)`}
                                </span>
                                <span className="tabular-nums" data-testid={`emp-commission-tier-revenue-${s.staff_id}-${t.month}`}>
                                  Selected-treatment revenue (base price): <strong>{fm(t.revenue)}</strong>
                                </span>
                                <span className="tabular-nums" data-testid={`emp-commission-tier-commission-${s.staff_id}-${t.month}`}>
                                  Commission at applied rate: <strong>{fm(t.commission)}</strong>
                                </span>
                                <span className="text-emerald-800/70">{t.lines} line{t.lines === 1 ? "" : "s"}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="border rounded-md overflow-hidden" data-testid={`emp-commission-breakdown-${s.staff_id}`}>
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-muted/30">
                                <TableHead className="uppercase text-[10px] tracking-wider">Date</TableHead>
                                <TableHead className="uppercase text-[10px] tracking-wider">Client</TableHead>
                                <TableHead className="uppercase text-[10px] tracking-wider">Treatment</TableHead>
                                <TableHead className="uppercase text-[10px] tracking-wider">Branch</TableHead>
                                <TableHead className="uppercase text-[10px] tracking-wider text-right">Amount</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {(s.lines || []).map((line) => {
                                const isDed = line.record_type === "deduction";
                                return (
                                  <TableRow key={line.id} data-testid={`emp-commission-line-${line.id}`}>
                                    <TableCell className="text-xs whitespace-nowrap">{prettyDate(line.date)}</TableCell>
                                    <TableCell className="text-xs">{line.customer_name || "—"}</TableCell>
                                    <TableCell className="text-xs">
                                      {line.treatment_name || "—"}
                                      {line.tiered && (
                                        <span
                                          className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 whitespace-nowrap"
                                          data-testid={`emp-commission-line-tier-${line.id}`}
                                        >
                                          {Number(line.tier_index) > 0 ? `Tier ${line.tier_index}` : "Base"} · {Number(line.tier_rate) || 0}%
                                          {Number(line.tier_share) > 0 && Number(line.tier_share) < 100
                                            ? ` · ${Number(line.tier_share)}% share`
                                            : ""}
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      className="text-xs text-muted-foreground"
                                      data-testid={`emp-commission-line-branch-${line.id}`}
                                    >
                                      {line.branch_name || "—"}
                                    </TableCell>
                                    <TableCell
                                      className={`text-right text-xs tabular-nums ${isDed ? "text-red-600" : ""}`}
                                      data-testid={`emp-commission-line-amount-${line.id}`}
                                    >
                                      {isDed
                                        ? `−${fm(Math.abs(line.amount))}`
                                        : fm(line.amount)}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
            {hasData && (
              <tfoot>
                <TableRow className="bg-muted/60 border-t">
                  <TableCell />
                  <TableCell colSpan={3} className="text-sm font-semibold uppercase text-[11px] tracking-wider">
                    Total · {view.staff.length} staff
                  </TableCell>
                  <TableCell
                    className="text-right text-sm font-semibold tabular-nums"
                    data-testid="emp-commission-total-earned"
                  >
                    {fm(totals.earned)}
                  </TableCell>
                  <TableCell
                    className="text-right text-sm font-semibold tabular-nums text-red-600"
                    data-testid="emp-commission-total-deductions"
                  >
                    {totals.deductions > 0 ? `−${fm(totals.deductions)}` : fm(0)}
                  </TableCell>
                  <TableCell
                    className="text-right text-sm font-semibold tabular-nums"
                    data-testid="emp-commission-total-net"
                  >
                    {fm(totals.net)}
                  </TableCell>
                </TableRow>
                {/* Per-branch sub-totals — one row per branch with activity
                    in the current filtered view. Collapses when there's
                    only one branch surviving the filter (adds no value). */}
                {view.branchTotals.length > 1 && view.branchTotals.map((bt) => (
                  <TableRow
                    key={bt.branch_id}
                    className="bg-muted/30"
                    data-testid={`emp-commission-branch-total-${bt.branch_id}`}
                  >
                    <TableCell />
                    <TableCell colSpan={3} className="text-xs text-muted-foreground pl-6">
                      <span className="inline-flex items-center gap-1.5">
                        <Building2 className="w-3 h-3" />
                        <span className="uppercase tracking-wider text-[10px]">
                          {bt.branch_name}
                        </span>
                      </span>
                    </TableCell>
                    <TableCell
                      className="text-right text-xs tabular-nums"
                      data-testid={`emp-commission-branch-earned-${bt.branch_id}`}
                    >
                      {fm(bt.earned)}
                    </TableCell>
                    <TableCell
                      className="text-right text-xs tabular-nums text-red-600"
                      data-testid={`emp-commission-branch-deducted-${bt.branch_id}`}
                    >
                      {bt.deductions > 0 ? `−${fm(bt.deductions)}` : fm(0)}
                    </TableCell>
                    <TableCell
                      className="text-right text-xs tabular-nums font-medium"
                      data-testid={`emp-commission-branch-net-${bt.branch_id}`}
                    >
                      {fm(bt.net)}
                    </TableCell>
                  </TableRow>
                ))}
              </tfoot>
            )}
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
